import { Option } from '@sapphire/result';

import { isSurveyChildParticipant, Survey, SurveyKind } from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';

// The keys a save runs the pipeline for: the active child always, plus each
// participating Traditional MC sibling whose targeting is unsaved.
export function resolveSaveSurveyForKeys(
  activeSurvey: Nullable<Survey>,
  activeChildKey: Nullable<SurveyChildKey>,
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  audienceChildren: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
): SurveyChildKey[] {
  return [
    ...Option.from(activeChildKey).mapOr([], (key) => [key]),
    ...siblingsWithUnsavedTargeting(
      activeSurvey,
      activeChildKey,
      coreChildren,
      audienceChildren,
    ),
  ];
}

// A Traditional MC locale is its own survey, so each one is saved on its own.
function siblingsWithUnsavedTargeting(
  activeSurvey: Nullable<Survey>,
  activeChildKey: Nullable<SurveyChildKey>,
  coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  audienceChildren: Record<SurveyChildKey, SurveyCreationChildAudienceState>,
): SurveyChildKey[] {
  return Option.from(activeSurvey)
    .filter((survey) => SurveyKind.isTraditionalMc(survey))
    .mapOr([], () =>
      Object.keys(audienceChildren).filter(
        (key) =>
          isSibling(key, activeChildKey) &&
          isSurveyChildParticipant(coreChildren[key]) &&
          hasUnsavedTargeting(audienceChildren[key]),
      ),
    );
}

function isSibling(
  key: SurveyChildKey,
  activeChildKey: Nullable<SurveyChildKey>,
): boolean {
  return String(key) !== String(activeChildKey);
}

function hasUnsavedTargeting({
  qualifications,
  quotas,
}: SurveyCreationChildAudienceState): boolean {
  return qualifications.isDirty || quotas.isDirty;
}
